import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import {
  LiveKitRoom,
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
  ConnectionStateToast,
  useConnectionState,
  useTracks,
  LocalUserChoices,
} from "@livekit/components-react";
import "@livekit/components-styles";
import { Track } from "livekit-client";
import VideoControlBar from "./VideoControlBar";
import PreJoinScreen from "./PreJoinScreen";

interface VideoCallModuleProps {
  onCallEnd?: () => void;
  isHost?: boolean;
  containerized?: boolean;
  token: string;
  initialUserChoices?: LocalUserChoices | null;
  skipPreJoin?: boolean;
}

const VideoCallModule: React.FC<VideoCallModuleProps> = ({
  onCallEnd,
  containerized = false,
  token,
  initialUserChoices,
  skipPreJoin = false,
}) => {
  const serverUrl = process.env.REACT_APP_LIVEKIT_SERVER_URL;
  const [showPreJoin, setShowPreJoin] = useState(!skipPreJoin);
  const [userChoices, setUserChoices] = useState<LocalUserChoices | null>(
    initialUserChoices || null,
  );

  const handlePreJoinComplete = (choices: LocalUserChoices) => {
    setUserChoices(choices);
    setShowPreJoin(false);
  };

  const handleConnected = () => {};
  const ConnectionStateTracker: React.FC = () => {
    const connectionState = useConnectionState();
    const [wasConnected, setWasConnected] = useState(false);

    useEffect(() => {
      if (connectionState === "connected") {
        setWasConnected(true);
      } else if (connectionState === "disconnected") {
        if (wasConnected && onCallEnd) {
          onCallEnd();
        }
      }
    }, [connectionState, onCallEnd, wasConnected]);

    return null;
  };

  const VideoLayout: React.FC = () => {
    const tracks = useTracks(
      [
        { source: Track.Source.Camera, withPlaceholder: true },
        { source: Track.Source.ScreenShare, withPlaceholder: false },
      ],
      { onlySubscribed: false },
    );

    return (
      <GridLayout tracks={tracks} style={{ height: "100%" }}>
        {/* @ts-ignore - LiveKit component type compatibility issue */}
        <ParticipantTile />
      </GridLayout>
    );
  };

  if (!token) {
    return (
      <Box
        sx={{
          ...(containerized
            ? { width: "100%", height: "100%" }
            : {
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1300,
              }),
          backgroundColor: "#1a1a1a",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: "white",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress sx={{ color: "white", mb: 2 }} />
          <Typography variant="h6">Loading video room...</Typography>
        </Box>
      </Box>
    );
  }

  if (showPreJoin) {
    return (
      <PreJoinScreen
        containerized={containerized}
        onJoin={handlePreJoinComplete}
      />
    );
  }

  const LKRoom = LiveKitRoom as any;

  return (
    <Box
      sx={{
        ...(containerized
          ? {
              width: "100%",
              height: "100%",
              borderRadius: "12px",
              overflow: "hidden",
            }
          : {
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1300,
            }),
        position: "relative",
      }}
    >
      <LKRoom
        connect={true}
        token={token}
        serverUrl={serverUrl}
        options={{
          publishDefaults: {
            videoDeviceId: userChoices?.videoDeviceId,
            audioDeviceId: userChoices?.audioDeviceId,
          },
          autoConnect: true,
          autoPublish: true,
          autoSubscribe: true,
        }}
        video={userChoices?.videoEnabled ?? true}
        audio={userChoices?.audioEnabled ?? true}
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#232323",
          borderRadius: "12px",
          overflow: "hidden",
        }}
        onConnected={handleConnected}
        data-lk-theme="default"
      >
        <ConnectionStateTracker />
        <Box sx={{ flex: 1, position: "relative" }}>
          <VideoLayout />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            padding: "16px",
            backgroundColor: "transparent",
          }}
        >
          <VideoControlBar />
        </Box>
        <RoomAudioRenderer />
        <ConnectionStateToast />
      </LKRoom>
    </Box>
  );
};

export default VideoCallModule;
